import { useCallback, useMemo, useReducer, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

import { useClient } from 'urql';

import { config } from '../../../config/config';
import type { IUserTransaction } from '../../account/queries/activity.graphql';
import type {
    IGetAllUserTransactionsQuery,
    IGetAllUserTransactionsQueryVariables,
} from '../queries/transactions.graphql';
import { GetAllUserTransactionsDocument } from '../queries/transactions.graphql';
import type { IUserTransactionState } from '../reducers/user-transactions.reducer';
import {
    userTransactionsReducer,
    USER_TRANSACTION_INITIAL_STATE,
} from '../reducers/user-transactions.reducer';
import {
    TransactionsViewType,
    TransactionsOperationType,
    TransactionsMediatorType,
} from '../transactions.interface';

function operationTypeFilter(view: TransactionsViewType) {
    switch (view) {
        case TransactionsViewType.DEPOSITS:
            return TransactionsOperationType.CREDIT;
        case TransactionsViewType.WITHDRAWALS:
            return TransactionsOperationType.DEBIT;
        case TransactionsViewType.GAMEPLAY:
            return null;
        case TransactionsViewType.SPORTS:
            return null;
    }
}

function mediatorTypeFilter(view: TransactionsViewType) {
    switch (view) {
        case TransactionsViewType.DEPOSITS:
            return TransactionsMediatorType.MONETARY;
        case TransactionsViewType.WITHDRAWALS:
            return TransactionsMediatorType.MONETARY;
        case TransactionsViewType.GAMEPLAY:
            return TransactionsMediatorType.CASINO;
        case TransactionsViewType.SPORTS:
            return TransactionsMediatorType.SPORTS;
    }
}

function mapToActivity(event?: IUserTransaction | null): IUserTransaction | undefined {
    if (!event) {
        return;
    }

    return {
        transactionId: event?.transactionId ?? '',
        tenantId: event.tenantId ?? '',
        userProfileId: event.userProfileId ?? '',
        walletId: event.walletId ?? '',
        walletType: event.walletType ?? '',
        lastUpdatedOn: event.lastUpdatedOn,
        mediatorId: event.mediatorId ?? '',
        mediatorType: event.mediatorType ?? '',
        operationType: event.operationType ?? '',
        previousBalanceAmount: event.previousBalanceAmount,
        balanceAmount: event.balanceAmount,
        transactionAmount: event.transactionAmount,
        currencyCode: event.currencyCode ?? '',
        transactionStatus: event.transactionStatus ?? '',
        transactionBatchId: event.transactionBatchId ?? '',
        additionalProperties: event.additionalProperties ?? {},
    };
}

export function useUserTransactions() {
    const client = useClient();
    const loadCount = useRef(0);
    const auth = useAuth();
    const userId = auth.user?.profile?.user_profile_id as string;

    const [state, dispatch] = useReducer(userTransactionsReducer, USER_TRANSACTION_INITIAL_STATE);

    const fetchMore = useCallback(
        async (currentState: typeof state) => {
            if (currentState.isLoading || !userId || !currentState.view) {
                return;
            }
            loadCount.current++;
            const currentCount = loadCount.current;

            dispatch({ type: 'loading' });

            const conditions = {
                tenantId: config.TENANT_ID,
                userProfileId: userId,
                mediatorType: mediatorTypeFilter(currentState.view),
            };

            const { data } = await client
                .query<IGetAllUserTransactionsQuery, IGetAllUserTransactionsQueryVariables>(
                    GetAllUserTransactionsDocument,
                    {
                        first: 50,
                        after: currentState.cursor || null,
                        inUserProfileId: conditions.userProfileId,
                        inDateFromEpoch: currentState.filters?.dateFrom,
                        inDateToEpoch: currentState.filters?.dateTo,
                        inMediatorType: conditions.mediatorType,
                        inOperationType: operationTypeFilter(currentState.view),
                        inTenantId: conditions.tenantId,
                    },
                    {
                        requestPolicy: 'network-only',
                    },
                )
                .toPromise();

            if (loadCount.current !== currentCount) {
                // Abort loading if we've already triggered another load.
                return;
            }

            const events = data?.userTransactions;
            const { endCursor, hasNextPage } = events?.pageInfo ?? {
                hasNextPage: false,
                endCursor: null,
            };
            const feed =
                events?.edges
                    .map((edge) => mapToActivity(edge.node))
                    .filter((item): item is NonNullable<typeof item> => !!item) ?? [];

            dispatch({
                type: 'complete',
                payload: {
                    feed,
                    hasMore: hasNextPage,
                    cursor: endCursor,
                },
            });
            dispatch({ type: 'loaded' });
        },
        [client, userId],
    );

    const updateFilters = useCallback(
        (view: TransactionsViewType, filters: IUserTransactionState['filters']) => {
            dispatch({ type: 'reset' });
            dispatch({ type: 'setFilters', payload: { filters, view } });
            fetchMore({
                ...USER_TRANSACTION_INITIAL_STATE,
                filters,
                view,
            });
        },
        [dispatch, fetchMore],
    );

    return useMemo(
        () => ({
            fetching: state.isLoading,
            fetchMore: () => fetchMore(state),
            feed: state.feed,
            hasMore: state.hasMore,
            updateFilters,
            view: state.view,
        }),
        [state, fetchMore, updateFilters],
    );
}
